/**
 * @file customer-columns.tsx
 * @description Колонки реестра клиентов для `ResponsiveList` (phase19.31,
 * зона «Клиенты и сделки»; SC46).
 *
 * Таблица от 1024: Клиент (имя + вид второй линией) · Телефон и почта ·
 * Заказов · Сумма покупок · Последний заказ · Пометки («блок», «риск»).
 * Число заказов и сумма — отдельными колонками справа, а не серой подписью.
 * На 1024–1279 «Заказов» и «Последний заказ» уходят второй линией под сумму
 * (`fold`). До 1024 — компактный список: «имя … сумма» / «телефон · почта» /
 * «N заказов · последний 22.09.2026 · пометки».
 */

import { CustomerMarks } from "../CustomerMarks";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { CustomerDto } from "../../../lib/api-customers";
import { CUSTOMER_TYPE_LABEL, customerMarks, customerTitle } from "../../../lib/crm-customer-view";
import { formatRub } from "../../../lib/crm-deal-view";
import { formatPhone } from "../../../lib/phone-format";
import { pluralRu } from "../../../lib/plural";
import { formatShopDate } from "../../../lib/shop-time";

/** «3 заказа». */
function ordersText(c: CustomerDto): string {
  return `${c.ordersCount} ${pluralRu(c.ordersCount, ["заказ", "заказа", "заказов"])}`;
}

/** «последний 22.09.2026» или `null`. */
function lastOrderText(c: CustomerDto): string | null {
  return c.lastOrderAt ? `последний ${formatShopDate(c.lastOrderAt)}` : null;
}

/** Телефон и почта одной строкой (телефон — в едином виде +7 912 345-67-89). */
function contactsLine(c: CustomerDto): string {
  return [c.phone ? formatPhone(c.phone) : null, c.email].filter(Boolean).join(" · ");
}

export const CUSTOMER_REGISTRY_COLUMNS: ListColumn<CustomerDto>[] = [
  {
    key: "customer",
    header: "Клиент",
    cell: (c) => (
      <TwoLineCell primary={<span className="font-medium">{customerTitle(c)}</span>} secondary={CUSTOMER_TYPE_LABEL[c.type]} />
    ),
    compact: "title",
    compactCell: (c) => customerTitle(c),
  },
  {
    key: "contacts",
    header: "Телефон и почта",
    width: "w-[220px]",
    cell: (c) =>
      c.phone || c.email ? (
        <TwoLineCell primary={c.phone ? formatPhone(c.phone) : (c.email ?? "")} secondary={c.phone ? (c.email ?? undefined) : undefined} />
      ) : (
        <span className="text-on-surface-variant">—</span>
      ),
    compact: "subtitle",
    compactCell: (c) => contactsLine(c) || null,
  },
  {
    key: "orders",
    header: "Заказов",
    width: "w-[88px]",
    align: "end",
    cell: (c) => <span className="tabular-nums">{c.ordersCount}</span>,
    compact: "meta",
    compactCell: ordersText,
    // На 1024–1279 под суммой одна линия «3 заказа · последний 22.09.2026».
    fold: { into: "ltv", cell: (c) => [ordersText(c), lastOrderText(c)].filter(Boolean).join(" · ") },
  },
  {
    key: "ltv",
    header: "Сумма покупок",
    width: "w-[132px]",
    align: "end",
    cell: (c) => <span className="tabular-nums whitespace-nowrap">{formatRub(c.lifetimeValueGross, "whole")}</span>,
    compact: "trailing",
    compactCell: (c) => <span className="type-body font-semibold tabular-nums whitespace-nowrap">{formatRub(c.lifetimeValueGross, "whole")}</span>,
  },
  {
    key: "lastOrder",
    header: "Последний заказ",
    width: "w-[132px]",
    cell: (c) => (c.lastOrderAt ? <span className="tabular-nums">{formatShopDate(c.lastOrderAt)}</span> : <span className="text-on-surface-variant">—</span>),
    compact: "meta",
    compactCell: lastOrderText,
    // Линия уже есть у «Заказов»: пустая линия высоты не имеет.
    fold: { into: "ltv", cell: () => null },
  },
  {
    key: "marks",
    header: "Пометки",
    width: "w-[160px]",
    cell: (c) => <CustomerMarks customer={c} emptyDash />,
    compact: "meta",
    compactCell: (c) => (customerMarks(c).length > 0 ? <CustomerMarks customer={c} /> : null),
  },
];
